/**
 * IdentityBadge — always-visible "logged in as" indicator.
 *
 * Mobile (<640px): compact icon button, tap to expand dropdown.
 * Desktop (>=640px): inline pill, click to expand dropdown.
 *
 * DROPDOWN: rendered via a fixed-position portal so it escapes any
 * ancestor overflow:hidden or height-clipped containers (e.g. the
 * Mi6BrandBand sticky header at 64px height).
 *
 * LOGOUT: immediate on click — no confirmation modal. The button text
 * switches to "Signing out…" for feedback, then:
 *   1. logout() → supabase.auth.signOut() + clearSession()
 *   2. window.location.href = '/#/login' (hard nav clears all in-memory state)
 *   3. window.location.reload() (clears PWA service-worker cache)
 */

import { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { getSession, getCurrentTenant, logout } from "@/lib/session";

// ── Helpers ────────────────────────────────────────────────────────────────

function truncate(str: string, max: number): string {
  return str.length <= max ? str : str.slice(0, max - 1) + "…";
}

// D-042 (2026-05-21): all-caps for every role label (not just BDR/AE/SE).
// Manager/Executive/Marketing previously rendered in title case which was
// inconsistent with the BDR/AE/SE chips elsewhere in the app.
const ROLE_LABELS: Record<string, string> = {
  ae: "AE",
  bdr: "BDR",
  se: "SE",
  manager: "MANAGER",
  executive: "EXECUTIVE",
  marketing: "MARKETING",
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

// ── SVG icons ──────────────────────────────────────────────────────────────

function PersonIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

function LogoutIconSvg() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}
      aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// ── Fixed-position dropdown (portal) ──────────────────────────────────────
// Using a portal means the dropdown is never clipped by a sticky/fixed
// ancestor with overflow:hidden or a fixed height.

interface DropdownPortalProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  email: string;
  role: string;
  tenantName: string;
  loggingOut: boolean;
  onLogout: () => void;
}

function DropdownPortal({ anchorRef, onClose, email, role, tenantName, loggingOut, onLogout }: DropdownPortalProps) {
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const panelRef = useRef<HTMLDivElement>(null);

  // Position the dropdown below the anchor button
  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, [anchorRef]);

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current && !panelRef.current.contains(target) &&
          anchorRef.current && !anchorRef.current.contains(target)) {
        onClose();
      }
    }
    // Use 'click' not 'mousedown' so the logout button's onClick fires first
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [anchorRef, onClose]);

  // Close on Escape
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        width: "256px",
        zIndex: 99999,
        background: "white",
        borderRadius: "12px",
        boxShadow: "0 10px 40px rgba(15,23,42,0.18), 0 2px 8px rgba(15,23,42,0.08)",
        border: "1px solid #E5E5E7",
        overflow: "hidden",
      }}
      data-identity-dropdown="true"
    >
      {/* Identity section */}
      <div style={{ padding: "12px 16px 10px", background: "#FAFAF7", borderBottom: "1px solid #EFEFEE" }}>
        <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8E8E94", marginBottom: "4px" }}>
          Signed in as
        </div>
        <div style={{ fontSize: "13px", fontWeight: 600, color: "#0B0B0F", wordBreak: "break-all", lineHeight: 1.3, marginBottom: "6px" }}>
          {email}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "2px 8px", borderRadius: "99px",
            fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
            background: "#0B0B0F", color: "white",
          }}>
            {roleLabel(role)}
          </span>
          <span style={{ fontSize: "11px", color: "#6B6B70", fontWeight: 500 }}>{tenantName}</span>
        </div>
      </div>

      {/* Logout button */}
      <div style={{ padding: "6px 8px" }}>
        <button
          id="aip-logout-btn"
          type="button"
          disabled={loggingOut}
          onClick={onLogout}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 12px",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 600,
            color: loggingOut ? "#8E8E94" : "#DC2626",
            background: "transparent",
            border: "none",
            cursor: loggingOut ? "not-allowed" : "pointer",
            transition: "background 0.12s",
            textAlign: "left",
          }}
          onMouseEnter={(e) => { if (!loggingOut) e.currentTarget.style.background = "#FEF2F2"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
        >
          <LogoutIconSvg />
          {loggingOut ? "Signing out…" : "Log out"}
        </button>
      </div>
    </div>,
    document.body
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function IdentityBadge() {
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const mobileBtnRef = useRef<HTMLButtonElement>(null);
  const desktopBtnRef = useRef<HTMLButtonElement>(null);

  const session = getSession();
  const tenant = getCurrentTenant();

  const handleClose = useCallback(() => setOpen(false), []);

  if (!session) return null;

  const email = session.email ?? "";
  const role = session.role ?? "";
  const tenantName = tenant?.name ?? `Tenant ${session.tenantId}`;

  // Which button is the anchor (whichever is visible at current viewport width)
  const anchorRef = window.innerWidth < 640 ? mobileBtnRef : desktopBtnRef;

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    setOpen(false);

    try {
      await logout();
    } catch {
      // Proceed regardless
    }

    // Hard navigate — wipes all React in-memory state
    window.location.href = "/#/login";
    // Brief delay then reload to bust PWA service-worker cache
    setTimeout(() => window.location.reload(), 100);
  }

  const buttonBaseStyle: React.CSSProperties = {
    cursor: "pointer",
    flexShrink: 0,
    outline: "none",
  };

  return (
    <>
      {/* ── Mobile: icon-only circle ─────────────────────────────────── */}
      <div className="sm:hidden" style={{ position: "relative", flexShrink: 0 }}>
        <button
          ref={mobileBtnRef}
          type="button"
          aria-label={`Signed in as ${email}. Tap to open account menu.`}
          aria-expanded={open}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          style={{
            ...buttonBaseStyle,
            width: "34px",
            height: "34px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: open ? "#0B0B0F" : "rgba(15,23,42,0.07)",
            color: open ? "white" : "#0B0B0F",
            border: `1.5px solid ${open ? "#0B0B0F" : "rgba(15,23,42,0.13)"}`,
            transition: "all 0.15s",
          }}
        >
          <PersonIcon />
        </button>
      </div>

      {/* ── Desktop: inline pill ─────────────────────────────────────── */}
      <div className="hidden sm:block" style={{ position: "relative", flexShrink: 0 }}>
        <button
          ref={desktopBtnRef}
          type="button"
          aria-label={`Signed in as ${email}. Click to open account menu.`}
          aria-expanded={open}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          style={{
            ...buttonBaseStyle,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            borderRadius: "8px",
            background: open ? "#0B0B0F" : "rgba(15,23,42,0.07)",
            color: open ? "white" : "#0B0B0F",
            border: `1.5px solid ${open ? "#0B0B0F" : "rgba(15,23,42,0.13)"}`,
            fontSize: "11px",
            fontWeight: 600,
            transition: "all 0.15s",
          }}
        >
          <PersonIcon />
          <span style={{ maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {truncate(email, 22)}
          </span>
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "1px 6px", borderRadius: "4px",
            fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
            background: open ? "rgba(255,255,255,0.2)" : "#0B0B0F",
            color: "white",
          }}>
            {roleLabel(role)}
          </span>
          <ChevronIcon open={open} />
        </button>
      </div>

      {/* ── Portal dropdown (escapes sticky header clipping) ─────────── */}
      {open && (
        <DropdownPortal
          anchorRef={anchorRef}
          onClose={handleClose}
          email={email}
          role={role}
          tenantName={tenantName}
          loggingOut={loggingOut}
          onLogout={handleLogout}
        />
      )}
    </>
  );
}
